import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useArtistData } from '@/features/artists/hooks';
import { useLoadingContext } from '@/hooks/useLoadingContext';
import { useAuth } from '@/hooks/useAuth';
import { 
  ArtistProfileHeader, 
  ArtistContentSection
} from '@/features/artists/components';
import { Footer, HamburgerMenu } from '@/components';
import ActionButtons from '@/components/ActionButtons';
import { ChevronRightIcon } from '@/components/icons';

/**
 * Main artist page component
 */
const ArtistPage: React.FC = () => {
  const { artistId, handle } = useParams<{ artistId: string; handle: string }>();
  const navigate = useNavigate();
  const { artist, events, loading, error } = useArtistData(artistId || '');
  const { isLoading } = useLoadingContext();
  const { isAuthenticated } = useAuth();

  // Get club handle from URL or artist data
  const clubHandle = handle || artist?.business?.handle || null;

  // The global loader will handle the loading state
  if (loading || isLoading) {
    return null; // Let the global loader handle the display
  }

  if (error) {
    return (
      <div className="bg-black text-white h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-medium text-red-400 mb-4">Error Loading Artist</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="bg-black text-white h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-medium text-white mb-4">Artist Not Found</h1>
          <p className="text-gray-400">The requested artist could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white">
      <HamburgerMenu clubHandle={clubHandle} isAuthenticated={isAuthenticated} />
      
      {/* Floating Action Buttons - positioned to left of hamburger menu */}
      <div className="fixed top-7 right-[5.5rem] z-[99] flex items-center gap-3">
        <ActionButtons
          entityId={artist.id}
          entityName={artist.name}
          entityType="artists"
          showBell={true}
        />
      </div>

      {/* Breadcrumb Navigation - Absolutely positioned over hero */}
      <div className="absolute top-8 left-0 right-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <nav className="text-sm text-gray-400 flex items-center bg-black/40 backdrop-blur-md px-4 py-2 rounded-full w-fit">
            <button 
              onClick={() => navigate(`/venues/${clubHandle}`)}
              className="hover:text-white transition-colors cursor-pointer"
            >
              {clubHandle || 'Venue'}
            </button>
            <ChevronRightIcon className="mx-2 w-4 h-4" />
            <span className="text-white">{artist.name}</span>
          </nav>
        </div>
      </div>
      
      <main>
        <ArtistProfileHeader artist={artist} />
        <ArtistContentSection artist={artist} events={events} isAuthenticated={isAuthenticated || false} />
      </main>
      <Footer />
    </div>
  );
};

export default ArtistPage;
